import {
  resolveAutoStorageTypeForMode,
  resolveBookingWizardCapacityLimit,
  resolveBookingWizardLocationStorageType as resolveLocationAutoStorageType,
  resolveBookingWizardStorageMode,
  normalizeLookupKey,
} from "api-client";
import type { BookingWizardStorageMode } from "api-client";
import type {
  ApiLocation,
  BookingWizardCapacityLimit,
  BookingWizardCosts,
  BookingWizardCustomer,
  BookingWizardLocation,
  BookingWizardOperatingHours,
  LookupDetailItem,
} from "api-client";
import type {
  CreateBookingCapacityTone,
  CreateBookingCostLine,
  CreateBookingEquipmentPieces,
  CreateBookingOperatingHoursDisplay,
  CreateBookingStepOneValues,
  CreateBookingStorageType,
  CreateBookingType,
} from "design-components";
import type { CreateBookingOption } from "design-components";
export {
  resolveMultiplePieceQuantityDecision,
  stripBookingWizardLocationLabel,
  type BookingWizardEnforcementModeKind,
  type MultiplePieceQuantityDecision,
} from "./booking-wizard-enforcement.utils";
import type { BookingWizardEnforcementModeKind } from "./booking-wizard-enforcement.utils";

import {
  bookingWizardCustomerOptions,
  type BookingWizardCustomerOptionMode,
  type BookingWizardCustomerOptionsParams,
} from "../../../shared/utils/wizard-customer-options";

export {
  bookingWizardCustomerOptions,
  type BookingWizardCustomerOptionMode,
  type BookingWizardCustomerOptionsParams,
};

export function bookingWizardLocationOptions(
  customers: BookingWizardCustomer[],
  customerId: string | null | undefined,
): CreateBookingOption[] {
  if (!customerId) return [];
  const customer = customers.find((c) => String(c.customerId) === customerId);
  if (!customer) return [];
  return customer.locations.map((loc) => ({
    value: String(loc.locationId),
    label: loc.locationName,
  }));
}

export function bookingWizardLocationStorageModes(
  customers: BookingWizardCustomer[],
  customerId: string | null | undefined,
): Record<string, BookingWizardStorageMode> {
  const customer = findBookingWizardCustomer(customers, customerId);
  if (!customer) return {};
  return customer.locations.reduce<Record<string, BookingWizardStorageMode>>((acc, loc) => {
    acc[String(loc.locationId)] = resolveBookingWizardStorageMode(loc.capacity);
    return acc;
  }, {});
}

/** Location id → auto storage for single-type sites; `null` when dual-type (user must choose). */
export function bookingWizardLocationStorageTypes(
  customers: BookingWizardCustomer[],
  customerId: string | null | undefined,
): Record<string, CreateBookingStorageType | null> {
  const customer = findBookingWizardCustomer(customers, customerId);
  if (!customer) return {};
  return customer.locations.reduce<Record<string, CreateBookingStorageType | null>>((acc, loc) => {
    const mode = resolveBookingWizardStorageMode(loc.capacity);
    acc[String(loc.locationId)] = resolveAutoStorageTypeForMode(mode);
    return acc;
  }, {});
}

/** Bookings list toolbar — customers from GET /api/bookings/customers (wizard payload). */
export function bookingListCustomerFilterOptions(
  customers: BookingWizardCustomer[],
): CreateBookingOption[] {
  return [{ value: "", label: "All customers" }, ...bookingWizardCustomerOptions(customers, { mode: "filter" })];
}

function bookingListOperatorLocationFilterOptions(
  operatorLocations: ApiLocation[] | null | undefined,
): CreateBookingOption[] {
  if (!operatorLocations?.length) {
    return [{ value: "", label: "All locations" }];
  }

  const sorted = [...operatorLocations].sort((a, b) =>
    String(a.label || "").localeCompare(String(b.label || ""), undefined, { sensitivity: "base" }),
  );

  return [
    { value: "", label: "All locations" },
    ...sorted.map((loc) => ({
      value: String(loc.locationId),
      label: loc.label || String(loc.locationId),
    })),
  ];
}

/**
 * Bookings list toolbar — locations for selected customer filter(s).
 * When no customer is selected, options come from GET /api/Operator/locations/dropdown.
 */
export function bookingListLocationFilterOptions(
  customers: BookingWizardCustomer[],
  selectedCustomerIds: string[],
  operatorLocations?: ApiLocation[] | null,
): CreateBookingOption[] {
  const customerIds = selectedCustomerIds.map((id) => id.trim()).filter(Boolean);
  if (customerIds.length === 0) {
    return bookingListOperatorLocationFilterOptions(operatorLocations);
  }

  const seen = new Set<number>();
  const locations: BookingWizardLocation[] = [];
  for (const customerId of customerIds) {
    const customer = customers.find((c) => String(c.customerId) === customerId);
    if (!customer) continue;
    for (const loc of customer.locations) {
      if (seen.has(loc.locationId)) continue;
      seen.add(loc.locationId);
      locations.push(loc);
    }
  }

  return [
    { value: "", label: "All locations" },
    ...locations.map((loc) => ({
      value: String(loc.locationId),
      label: loc.locationName,
    })),
  ];
}

/** Location ids still valid after customer filter change. */
export function pruneBookingListLocationFilterIds(
  customers: BookingWizardCustomer[],
  selectedCustomerIds: string[],
  selectedLocationIds: string[],
  operatorLocations?: ApiLocation[] | null,
): string[] {
  const allowed = new Set(
    bookingListLocationFilterOptions(customers, selectedCustomerIds, operatorLocations)
      .map((o) => o.value)
      .filter(Boolean),
  );
  return selectedLocationIds.filter((id) => allowed.has(id));
}

export function findBookingWizardCustomer(
  customers: BookingWizardCustomer[],
  customerId: string | null | undefined,
): BookingWizardCustomer | null {
  if (!customerId) return null;
  return customers.find((c) => String(c.customerId) === customerId) ?? null;
}

export function findBookingWizardLocation(
  customer: BookingWizardCustomer | null,
  locationId: string | null | undefined,
): BookingWizardLocation | null {
  if (!customer || !locationId) return null;
  return customer.locations.find((l) => String(l.locationId) === locationId) ?? null;
}

const SEAL_CAPTURE_TOGGLE_LABEL: Record<CreateBookingType, string> = {
  pickup: "(at out gate)",
  dropoff: "(at in gate)",
};

/** AV-113 — toggle copy by booking type (FR-AV113-006). */
export function sealCaptureToggleLabel(bookingType: CreateBookingType): string {
  return SEAL_CAPTURE_TOGGLE_LABEL[bookingType];
}

/** AV-113 — default toggle from customer flags (FR-AV113-002–004). */
export function sealCaptureRequiredByDefault(
  customer: BookingWizardCustomer | null,
  bookingType: CreateBookingType,
): boolean {
  if (!customer) return false;
  return bookingType === "pickup" ? customer.requiredForPickups : customer.requiredForDeliveries;
}

/** Draft value when re-entering equipment step; else customer default. */
export function initialSealCaptureRequire(
  draft: { requireSealCapture: boolean } | null | undefined,
  customer: BookingWizardCustomer | null,
  bookingType: CreateBookingType,
): boolean {
  return draft?.requireSealCapture ?? sealCaptureRequiredByDefault(customer, bookingType);
}

/** Edit wizard — seal toggle from GET prefill only (FR-AV186-003); no customer default. */
export function editHydratedSealCaptureRequire(
  draft: { requireSealCapture: boolean } | null | undefined,
  hydratedSealCaptureEnabled: boolean | null | undefined,
): boolean {
  if (draft != null) return draft.requireSealCapture;
  if (hydratedSealCaptureEnabled != null) return hydratedSealCaptureEnabled;
  return false;
}

/** True when create/edit should reset seal equipment draft (FR-AV113-009–010). */
export function stepOneSealContextChanged(
  prev: CreateBookingStepOneValues,
  next: CreateBookingStepOneValues,
): boolean {
  return (
    prev.customerId !== next.customerId ||
    prev.bookingType !== next.bookingType ||
    prev.storageType !== next.storageType ||
    prev.equipmentPieces !== next.equipmentPieces
  );
}

export function resolveBookingWizardLocationTimeZone(
  customers: BookingWizardCustomer[],
  customerId: string,
  locationId: string,
): string | null {
  const location = findBookingWizardLocation(findBookingWizardCustomer(customers, customerId), locationId);
  const tz = location?.operatingHours.timezone?.trim();
  return tz || null;
}

export function buildOperatingHoursDisplay(
  hours: BookingWizardOperatingHours | null | undefined,
): CreateBookingOperatingHoursDisplay | null {
  if (!hours) return null;
  const tz = hours.timezone?.trim() || "";
  const tzSuffix = tz ? ` ${tz}` : "";
  return {
    weekdayLine: `Mon - Fri: ${formatApiTime(hours.weekday.open)} - ${formatApiTime(hours.weekday.close)}${tzSuffix}`,
    weekendLine: `Sat - Sun: ${formatApiTime(hours.weekend.open)} - ${formatApiTime(hours.weekend.close)}${tzSuffix}`,
  };
}

/** Numeric cost field sent on create booking — mirrors capacity/cost drawer day rate. */
export interface BookingCostPayloadFields {
  dayRate: number | null;
}

export interface CreateBookingCapacityCostLabels {
  action: "create" | "end";
  primaryActionLabel: string;
  primaryCapacityLabel: string;
  stackSpotsFullLabel: string;
  remainingSpotsLabel: string;
  remainingSpotsTone: CreateBookingCapacityTone;
  wheeledSpotsFullLabel: string | null;
  wheeledRemainingSpotsLabel: string | null;
  wheeledRemainingSpotsTone: CreateBookingCapacityTone;
  /** Default day rate from branch `dayRate` (fallback `costs.baseBookingFee`); null when unset. */
  defaultDayRate: number | null;
  costPayload: BookingCostPayloadFields;
  /** Legacy line items — edit wizard until edit API migrates (FR-AV131-API). */
  costLines: CreateBookingCostLine[];
  totalAmount: string;
  showCostEstimate: boolean;
  warningMessage: string | null;
  warningMessageTone: Exclude<CreateBookingCapacityTone, "success"> | null;
}

export interface BuildCapacityCostLabelsParams {
  location: BookingWizardLocation | null | undefined;
  storageType: CreateBookingStorageType | null;
  bookingType: CreateBookingType | null;
  /** Multipiece MAB-only schedule — hides cost estimate (FR-AV131-006, FR-AV132-011). */
  equipmentPieces?: CreateBookingEquipmentPieces | null;
  /** AV-93 — fee/surcharge basis is scheduled datetime. */
  scheduledDateTime: Date | null | undefined;
  locationTimeZone: string | null | undefined;
  enforcementModeLookupItems: LookupDetailItem[];
}

export interface StackEquipmentCapacityContext {
  stackSpotsFullLabel: string;
  remainingSpotsLabel: string;
  remainingSpotsTone: CreateBookingCapacityTone;
  remainingSpots: number;
  /** Mode 27 — max quantity including grace (total + grace − filled). */
  graceInclusiveMaxQuantity: number;
  enforcementMode: BookingWizardEnforcementModeKind;
}

interface ResolvedCapacityContext {
  capacityLimit: BookingWizardCapacityLimit | null;
  capacityForEnforcement: BookingWizardCapacityLimit | null;
  enforcementMode: BookingWizardEnforcementModeKind;
  displayedCapacity: number;
  remaining: number;
  filledCapacity: number;
  costs: BookingWizardCosts | undefined;
}

function resolveCapacityContext(params: BuildCapacityCostLabelsParams): ResolvedCapacityContext {
  const selectedStorageType = params.storageType ?? resolveBookingWizardLocationStorageType(params.location);
  const capacityLimit = resolveCapacityLimit(params.location, selectedStorageType);
  const capacityForEnforcement = adjustCapacityLimitForBookingType(capacityLimit, params.bookingType);
  const enforcementMode = resolveEnforcementModeKind(
    capacityLimit?.enforcementMode,
    params.enforcementModeLookupItems,
  );
  const displayedCapacity = resolveDisplayedCapacity(capacityLimit, enforcementMode);
  const remaining = resolveRemainingSpots(capacityForEnforcement, enforcementMode);
  const filledCapacity = capacityForEnforcement?.filledCapacity ?? 0;

  return {
    capacityLimit,
    capacityForEnforcement,
    enforcementMode,
    displayedCapacity,
    remaining,
    filledCapacity,
    costs: capacityLimit?.costs,
  };
}

/** FR-AV131-012–015 — capacity readout + enforcement inputs for multipiece equipment drawer. */
export function buildStackEquipmentCapacityContext(
  params: BuildCapacityCostLabelsParams,
): StackEquipmentCapacityContext {
  const ctx = resolveCapacityContext(params);
  const remainingSpotsTone = resolveRemainingSpotsTone(
    ctx.capacityForEnforcement,
    ctx.enforcementMode,
    ctx.remaining,
    resolveCapacityDecision(ctx.enforcementMode, ctx.capacityForEnforcement),
  );

  return {
    stackSpotsFullLabel: `${ctx.filledCapacity} / ${ctx.displayedCapacity} spots full`,
    remainingSpotsLabel: `${ctx.remaining} spot${ctx.remaining === 1 ? "" : "s"} remaining`,
    remainingSpotsTone,
    remainingSpots: ctx.remaining,
    graceInclusiveMaxQuantity: resolveGraceInclusiveMaxQuantity(ctx.capacityForEnforcement),
    enforcementMode: ctx.enforcementMode,
  };
}

export function buildCapacityCostLabels(params: BuildCapacityCostLabelsParams): CreateBookingCapacityCostLabels {
  const selectedStorageType = params.storageType ?? resolveBookingWizardLocationStorageType(params.location);
  const ctx = resolveCapacityContext(params);
  const { capacityForEnforcement, enforcementMode, costs } = ctx;
  const filledCapacity = ctx.filledCapacity;
  const displayedCapacity = ctx.displayedCapacity;
  const remaining = ctx.remaining;
  const enforcementDecision = resolvePickupSafeCapacityDecision(
    params.bookingType,
    resolveCapacityDecision(enforcementMode, capacityForEnforcement),
  );
  const remainingSpotsTone = resolveRemainingSpotsTone(
    capacityForEnforcement,
    enforcementMode,
    remaining,
    enforcementDecision,
  );
  const defaultDayRate = resolveCapacityLimitDayRate(ctx.capacityLimit);
  const temporalSurcharges = resolveTemporalSurcharges(
    params.scheduledDateTime,
    params.location?.operatingHours,
    params.locationTimeZone,
    costs,
  );
  const legacyCostLines = buildCostLines(defaultDayRate, enforcementDecision.includeBaseSurcharge, temporalSurcharges);
  const legacyTotal = legacyCostLines.reduce((sum, line) => sum + line.value, 0);
  const isMultipieceMabOnlySchedule =
    params.equipmentPieces === "multiple_pieces" &&
    ((params.bookingType === "dropoff" && params.storageType === "stack") ||
      params.bookingType === "pickup");
  const showCostEstimate = !isMultipieceMabOnlySchedule && enforcementDecision.action === "create";

  return {
    action: enforcementDecision.action,
    primaryActionLabel: enforcementDecision.action === "end" ? "End booking" : "Create booking",
    primaryCapacityLabel: selectedStorageType === "wheeled" ? "Wheeled" : "Stack",
    stackSpotsFullLabel: `${filledCapacity} / ${displayedCapacity} spots full`,
    remainingSpotsLabel: `${remaining} spot${remaining === 1 ? "" : "s"} remaining`,
    remainingSpotsTone,
    wheeledSpotsFullLabel: null,
    wheeledRemainingSpotsLabel: null,
    wheeledRemainingSpotsTone: remainingSpotsTone,
    defaultDayRate,
    costPayload: { dayRate: defaultDayRate },
    costLines: legacyCostLines.map(({ label, value }) => ({ label, amount: formatUsd(value) })),
    totalAmount: formatUsd(legacyTotal),
    showCostEstimate,
    warningMessage: enforcementDecision.warningMessage,
    warningMessageTone: resolveWarningMessageTone(enforcementDecision),
  };
}

export function resolveBookingWizardLocationStorageType(
  location: BookingWizardLocation | null | undefined,
): CreateBookingStorageType | null {
  return resolveLocationAutoStorageType(location) as CreateBookingStorageType | null;
}

/** AV-99 — costs for the active storage branch (dropoff) or location default (pickup). */
export function resolveBookingWizardBranchCosts(
  location: BookingWizardLocation | null | undefined,
  storageType: CreateBookingStorageType | null,
  bookingType: CreateBookingType | null,
): BookingWizardCosts | null {
  const effectiveStorageType =
    bookingType === "dropoff"
      ? storageType
      : resolveBookingWizardLocationStorageType(location);
  const limit = resolveBookingWizardCapacityLimit(location, effectiveStorageType);
  return limit?.costs ?? null;
}

export function resolveDefaultDayRate(
  location: BookingWizardLocation | null | undefined,
  storageType: CreateBookingStorageType | null,
  bookingType: CreateBookingType | null,
): number | null {
  const effectiveStorageType =
    bookingType === "dropoff"
      ? storageType
      : resolveBookingWizardLocationStorageType(location);
  return resolveCapacityLimitDayRate(
    resolveCapacityLimit(location, effectiveStorageType as CreateBookingStorageType | null),
  );
}

/** FR-AV132-DR-002–003 / FR-DRN — branch dayRate; explicit null stays null (no fee coerce). */
export function resolveCapacityLimitDayRate(
  limit: BookingWizardCapacityLimit | null | undefined,
): number | null {
  if (!limit) return null;
  const { dayRate, costs } = limit;
  // Explicit API null → blank UI (FR-DRN-001); do not fall back to baseBookingFee.
  if (dayRate === null) return null;
  if (dayRate !== undefined && Number.isFinite(dayRate) && dayRate >= 0) return dayRate;
  // Field absent: legacy AV-132 fallback to costs.baseBookingFee.
  const fee = costs.baseBookingFee;
  if (fee !== undefined && Number.isFinite(fee) && fee >= 0) return fee;
  return null;
}

function formatApiTime(time24: string): string {
  const trimmed = time24.trim();
  const match = /^(\d{1,2}):(\d{2})$/.exec(trimmed);
  if (!match) return trimmed;
  const hour = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return trimmed;
  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${String(minute).padStart(2, "0")} ${period}`;
}

function formatUsd(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

type EnforcementModeKind = BookingWizardEnforcementModeKind;
const LOW_REMAINING_WARNING_SPOTS = 5;
const ENFORCEMENT_MODE_DENY_ENTRY_ID = 25;
const ENFORCEMENT_MODE_WARNING_UNLIMITED_ID = 26;
const ENFORCEMENT_MODE_WARNING_LIMITED_ID = 27;

interface CapacityDecision {
  action: "create" | "end";
  includeBaseSurcharge: boolean;
  warningMessage: string | null;
}

interface TemporalSurcharge {
  label: string;
  value: number;
}

function resolveCapacityLimit(
  location: BookingWizardLocation | null | undefined,
  storageType: CreateBookingStorageType | null,
): BookingWizardCapacityLimit | null {
  if (storageType === "wheeled") return location?.capacity.wheeled ?? null;
  return location?.capacity.stack ?? null;
}

/** Pickup frees one spot — treat filled capacity as one lower for labels and enforcement. */
function adjustCapacityLimitForBookingType(
  capacity: BookingWizardCapacityLimit | null,
  bookingType: CreateBookingType | null,
): BookingWizardCapacityLimit | null {
  if (!capacity || bookingType !== "pickup") return capacity;
  return {
    ...capacity,
    filledCapacity: Math.max(0, capacity.filledCapacity - 1),
  };
}

/** Pickup never applies base surcharge; cannot hard-stop with End booking. */
function resolvePickupSafeCapacityDecision(
  bookingType: CreateBookingType | null,
  decision: CapacityDecision,
): CapacityDecision {
  if (bookingType !== "pickup") return decision;

  if (decision.action === "end") {
    return { action: "create", includeBaseSurcharge: false, warningMessage: null };
  }

  const warningMessage = decision.warningMessage?.includes("base surcharge")
    ? "Capacity is above the configured limit."
    : decision.warningMessage;

  return {
    action: decision.action,
    includeBaseSurcharge: false,
    warningMessage,
  };
}

function resolveEnforcementModeKind(
  raw: string | null | undefined,
  lookupItems: LookupDetailItem[],
): EnforcementModeKind {
  const rawText = String(raw ?? "").trim();
  const rawId = Number(rawText);
  if (rawId === ENFORCEMENT_MODE_DENY_ENTRY_ID) return "deny";
  if (rawId === ENFORCEMENT_MODE_WARNING_UNLIMITED_ID) return "warning_unlimited";
  if (rawId === ENFORCEMENT_MODE_WARNING_LIMITED_ID) return "warning_limited";

  const lookupValue = Number.isFinite(rawId) ? lookupItems.find((item) => item.id === rawId)?.value : undefined;
  const normalized = normalizeLookupKey(lookupValue ?? rawText);

  if (!normalized) return "unknown";
  if (normalized.includes("deny") || normalized === "strict") return "deny";
  if (normalized.includes("unlimited")) return "warning_unlimited";
  if (normalized.includes("warning") || normalized === "soft") return "warning_limited";
  return "unknown";
}

function resolveCapacityDecision(
  enforcementMode: EnforcementModeKind,
  capacity: BookingWizardCapacityLimit | null,
): CapacityDecision {
  if (!capacity) return { action: "create", includeBaseSurcharge: false, warningMessage: null };

  const totalCapacity = capacity.totalCapacity;
  const filledCapacity = capacity.filledCapacity;
  const graceLimit = totalCapacity + Math.max(0, capacity.graceCapacity);

  if (enforcementMode === "deny" && filledCapacity >= totalCapacity) {
    return {
      action: "end",
      includeBaseSurcharge: false,
      warningMessage: "Capacity is full for the selected storage type. Please try with different storage type or location.",
    };
  }

  if (enforcementMode === "warning_limited") {
    if (filledCapacity >= graceLimit) {
      return {
        action: "end",
        includeBaseSurcharge: false,
        warningMessage: "Capacity is above the allowed grace limit.",
      };
    }
    if (filledCapacity >= totalCapacity) {
      return {
        action: "create",
        includeBaseSurcharge: true,
        warningMessage: "Capacity is above the configured limit. A base surcharge applies.",
      };
    }
  }

  if (enforcementMode === "warning_unlimited" && filledCapacity >= totalCapacity) {
    return {
      action: "create",
      includeBaseSurcharge: true,
      warningMessage: "Capacity is above the configured limit. A base surcharge applies.",
    };
  }

  return { action: "create", includeBaseSurcharge: false, warningMessage: null };
}

function resolveGraceInclusiveMaxQuantity(
  capacity: BookingWizardCapacityLimit | null,
): number {
  if (!capacity) return 0;
  const graceLimit = capacity.totalCapacity + Math.max(0, capacity.graceCapacity);
  return Math.max(0, graceLimit - capacity.filledCapacity);
}

function resolveRemainingSpots(
  capacity: BookingWizardCapacityLimit | null,
  enforcementMode: EnforcementModeKind,
): number {
  if (!capacity) return 0;

  const totalRemaining = capacity.totalCapacity - capacity.filledCapacity;
  if (enforcementMode === "warning_limited" && totalRemaining <= 0) {
    const graceLimit = capacity.totalCapacity + Math.max(0, capacity.graceCapacity);
    return Math.max(0, graceLimit - capacity.filledCapacity);
  }
  return Math.max(0, totalRemaining);
}

function resolveDisplayedCapacity(
  capacity: BookingWizardCapacityLimit | null,
  enforcementMode: EnforcementModeKind,
): number {
  if (!capacity) return 0;
  if (enforcementMode === "warning_limited") {
    return capacity.totalCapacity + Math.max(0, capacity.graceCapacity);
  }
  return capacity.totalCapacity;
}

function resolveRemainingSpotsTone(
  capacity: BookingWizardCapacityLimit | null,
  enforcementMode: EnforcementModeKind,
  remaining: number,
  decision: CapacityDecision,
): CreateBookingCapacityTone {
  if (!capacity) return "success";
  if (decision.action === "end") return "error";
  if (enforcementMode === "warning_unlimited" && capacity.filledCapacity >= capacity.totalCapacity) return "warning";
  if (enforcementMode === "warning_limited" && capacity.filledCapacity >= capacity.totalCapacity) return "warning";
  if (remaining <= 0) return "error";
  if (remaining <= LOW_REMAINING_WARNING_SPOTS) return "warning";
  return "success";
}

function resolveWarningMessageTone(
  decision: CapacityDecision,
): Exclude<CreateBookingCapacityTone, "success"> | null {
  if (!decision.warningMessage) return null;
  return decision.action === "end" ? "error" : "warning";
}

function resolveTemporalSurcharges(
  scheduledDateTime: Date | null | undefined,
  hours: BookingWizardOperatingHours | null | undefined,
  timeZone: string | null | undefined,
  costs: BookingWizardCosts | null | undefined,
): TemporalSurcharge[] {
  if (!scheduledDateTime || !hours || !timeZone || !costs) return [];

  const parts = getZonedDateParts(scheduledDateTime, timeZone);
  if (!parts) return [];

  const baseFee = costs.baseBookingFee ?? 0;
  const lines: TemporalSurcharge[] = [];
  const window = parts.isWeekend ? hours.weekend : hours.weekday;

  if (parts.isWeekend && costs.weekendMultiplier > 1) {
    lines.push({
      label: `Weekend (x${formatMultiplier(costs.weekendMultiplier)})`,
      value: baseFee * (costs.weekendMultiplier - 1),
    });
    return lines;
  }

  if (isAfterHours(parts.minutes, window) && costs.storageFeePerHour > 1) {
    lines.push({
      label: `After hours (x${formatMultiplier(costs.storageFeePerHour)})`,
      value: baseFee * (costs.storageFeePerHour - 1),
    });
  }

  return lines;
}

function buildCostLines(
  baseFee: number | null,
  includeBaseSurcharge: boolean,
  temporalSurcharges: TemporalSurcharge[],
): Array<{ label: string; value: number }> {
  const lines: Array<{ label: string; value: number }> = [];
  if (includeBaseSurcharge) {
    lines.push({ label: "Base surcharge fee", value: baseFee ?? 0 });
  }
  lines.push(...temporalSurcharges);
  return lines;
}

function getZonedDateParts(date: Date, timeZone: string): { isWeekend: boolean; minutes: number } | null {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(date);
    const weekday = parts.find((part) => part.type === "weekday")?.value.toLowerCase();
    const hour = Number(parts.find((part) => part.type === "hour")?.value);
    const minute = Number(parts.find((part) => part.type === "minute")?.value);
    if (!weekday || !Number.isFinite(hour) || !Number.isFinite(minute)) return null;
    return {
      isWeekend: weekday.startsWith("sat") || weekday.startsWith("sun"),
      minutes: hour * 60 + minute,
    };
  } catch {
    return null;
  }
}

function isAfterHours(minutes: number, window: { open: string; close: string }): boolean {
  const open = parseTimeToMinutes(window.open);
  const close = parseTimeToMinutes(window.close);
  if (open === null || close === null) return false;
  return minutes < open || minutes >= close;
}

function parseTimeToMinutes(time24: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time24.trim());
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  return hour * 60 + minute;
}

function formatMultiplier(multiplier: number): string {
  return Number.isInteger(multiplier) ? String(multiplier) : String(multiplier);
}
